import { DataGrid } from '@mui/x-data-grid';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { useQueryClient } from 'react-query';

import useMediaPlansTableColumns from './hooks/useMediaPlansTableColumns';
import CreatePlanButton from '../CreatePlanButton/CreatePlanButton';
import usePlanStore from '../../../../store/planStore';
import TableSearchbar from './TableSearchbar/TableSearchbar';
import useMediaPlansInfinite from './hooks/useMediaPlansInfinite';
import TotalsFooter from './TotalsFooter/TotalsFooter';
import DeletePlanConfirmationModal from './DeletePlanConfirmationModal/DeletePlanConfirmationModal';
import ComparedPlansBar from './ComparedPlansBar/ComparedPlansBar';
import AcceptPlanFlow from '../Plan/PlanSummary/AcceptPlanFlow/AcceptPlanFlow';

import statuses from './helpers/statuses';
import useAcceptUnlockMediaPlan from '../Plan/PlanSummary/hooks/useAcceptUnlockMediaPlan';

function CustomNoRowsOverlay() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'gray',
        fontSize: '16px',
      }}
    >
      <Typography variant="body1" textAlign="center">
        There are no media plans created yet. <br /> Click the blue &quot;Create
        plan&quot; button and create your first media plan.
      </Typography>
    </Box>
  );
}

type PendingAcceptUnlock = {
  id: string;
  status: string;
  action: 'accept' | 'revert';
} | null;

export default function MediaPlansTable() {
  const queryClient = useQueryClient();
  const { setIsPlanCreating, setIsLocked, comparedPlans } = usePlanStore();
  const handleCreatePlan = () => {
    setIsLocked(false);
    setIsPlanCreating(true);
  };

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const { palette } = useTheme();

  const gridRef = useRef<HTMLDivElement | null>(null);

  const {
    plans,
    fetchNextPage,
    hasNextPage,
    total,
    isFetchingNextPage,
    isLoading,
  } = useMediaPlansInfinite(searchTerm);

  const debouncedSetSearchTerm = useMemo(
    () => debounce((query: string) => setSearchTerm(query), 300),
    []
  );
  const handleSearch = (query: string) => debouncedSetSearchTerm(query);

  const handleScroll = useCallback(() => {
    if (!gridRef.current || !hasNextPage || isFetchingNextPage) return;

    const scrollContainer = gridRef.current.querySelector(
      '.MuiDataGrid-virtualScroller'
    );
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

    if (scrollTop + clientHeight >= scrollHeight - 150) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!gridRef.current) return undefined;

    const scrollContainer = gridRef.current.querySelector(
      '.MuiDataGrid-virtualScroller'
    );
    if (!scrollContainer) return undefined;

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const [pendingAcceptUnlock, setPendingAcceptUnlock] =
    useState<PendingAcceptUnlock>(null);

  const [acceptUnlockError, setAcceptUnlockError] = useState<string | null>(
    null
  );

  const acceptPlanOpen = Boolean(pendingAcceptUnlock);
  const currentStatus = pendingAcceptUnlock?.status ?? '';

  const handleCloseAcceptPlan = () => {
    setPendingAcceptUnlock(null);
    setAcceptUnlockError(null);
  };

  const patchPlanInCache = useCallback(
    (id: string, patch: Record<string, any>) => {
      queryClient.setQueryData(['get-media-plans', searchTerm], (old: any) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: {
              ...page.data,
              mediaPlans: (page.data?.mediaPlans ?? []).map((p: any) =>
                p.id === id ? { ...p, ...patch } : p
              ),
            },
          })),
        };
      });
    },
    [queryClient, searchTerm]
  );

  const { asyncAcceptUnlockMediaPlanTrigger, isLoading: isAcceptingMediaPlan } =
    useAcceptUnlockMediaPlan();

  const handleProceedAcceptUnlock = () => {
    if (!pendingAcceptUnlock) return;

    const { id, action } = pendingAcceptUnlock;

    // ✅ clear any previous error before retry
    setAcceptUnlockError(null);

    asyncAcceptUnlockMediaPlanTrigger(
      { id, action },
      {
        onSuccess: (res: any) => {
          const updatedPlan = res?.data?.mediaPlan ?? res?.data ?? null;

          if (updatedPlan && updatedPlan.id) {
            patchPlanInCache(id, updatedPlan);
          } else {
            patchPlanInCache(id, {
              statusInfo: {
                ...(plans.find((p) => p.id === id)?.statusInfo ?? {}),
                current:
                  action === 'accept' ? statuses.ACCEPTED : statuses.BOOKED,
              },
            });
          }

          setPendingAcceptUnlock(null);
          setAcceptUnlockError(null);
        },
        onError: (err: any) => {
          const message =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Something went wrong';

          setAcceptUnlockError(message);
        },
      }
    );
  };

  const { columns, deletePlan, deletedId } = useMediaPlansTableColumns(
    setOpenDeleteModal,
    {
      onAcceptUnlockRequest: ({ id, status, action }) => {
        setPendingAcceptUnlock({ id, status, action });
        setAcceptUnlockError(null); // ✅ clear error when opening dialog
      },
    }
  );

  const handleDeletePlan = () => {
    deletePlan(deletedId ?? '');
    setOpenDeleteModal(false);
  };

  const planIds = useMemo(() => Object.keys(comparedPlans), [comparedPlans]);

  const handleComparePlans = () => {
    const url = new URL(
      `/media-planning/compare?ids=${planIds.join(',')}`,
      window.location.origin
    );
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Stack width="100%" px="20px">
        <Stack
          flexDirection="row"
          alignItems="center"
          gap="32px"
          width="100%"
          mb="34px"
          mt="32px"
        >
          <TableSearchbar onSearch={handleSearch} />
          <CreatePlanButton handleCreatePlan={handleCreatePlan} />
        </Stack>

        <Box sx={{ height: 600, width: '100%' }} ref={gridRef}>
          <DataGrid
            rows={plans}
            columns={columns}
            rowHeight={64}
            // MIT DataGrid forces pagination on and slices rows to pageSize
            // (max 100) in client mode, which capped the infinite scroll.
            // Server mode renders every row we have loaded so far.
            paginationMode="server"
            rowCount={total}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 !== 0 ? 'highlighted' : ''
            }
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                px: '20px',
                color: palette.mediaPlanTable.textColor,
                fontSize: '14px',
                fontWeight: 600,
              },
              '& .MuiDataGrid-columnHeader': {
                px: '20px',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'uppercase',
                },
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: `1px solid ${palette.mediaPlanTable.headerDivider}`,
              },
              '& .MuiDataGrid-row': {
                borderBottom: 'none',
              },
              '.MuiDataGrid-iconButtonContainer': {
                ml: 1,
              },
              backgroundColor: 'primary.contrastText',
              '& .highlighted': {
                backgroundColor: 'mediaPlanTable.background',
              },
            }}
            hideFooter
            loading={isLoading || isFetchingNextPage || isAcceptingMediaPlan}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Box>

        <TotalsFooter total={total} count={plans.length} />
        <ComparedPlansBar onCompare={handleComparePlans} />
      </Stack>

      <AcceptPlanFlow
        open={acceptPlanOpen}
        onClose={handleCloseAcceptPlan}
        onSuccess={handleProceedAcceptUnlock}
        status={currentStatus}
        isLoading={isAcceptingMediaPlan}
        error={acceptUnlockError}
      />

      <DeletePlanConfirmationModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleDeletePlan}
      />
    </>
  );
}
